// Where one page already exists in other languages.
//
// Two screens ask this and they ask it for different reasons — the view screen
// so an author can see the group and walk into it, the Translate screen so it
// can say what is left to do — so the answer lives here rather than on either
// of them. It is the analogue of ./edit/locales for the same reason that file
// gives: what a screen SHOWS about a site's languages has to agree with what
// the service does with them, and a second copy is how that stops being true.
//
// Nothing here is the storage model. A page is one ROW per language, joined to
// its siblings by a shared `translationKey`; api/code/src/render/resolve.js
// reads the same grouping to emit hreflang, and routes/.../pages/list/groups.js
// makes it for the listing. This is the third reader, and the smallest.
import React from "react";
import { Table } from "reactstrap";
import { Link } from "react-router-dom";
import { localeOf } from "@common/render/page";
import { PublishBadge, publishStateOf } from "../published";

// How many of the site's pages are read to find one page's translations.
//
// One page of the listing, where `model/page.translate` walks up to twenty. The
// asymmetry is deliberate and is the difference between a REFUSAL and a HINT:
// the model has to be right, because it is the only thing standing between the
// table and two pages claiming one language; the screens only ever draw a list
// or narrow a picker with what this returns. A group member past this bound is
// a language missing from the table and an option offered that the server then
// rejects — which is exactly the screen there was before any of this — rather
// than a wrong page.
//
// Both callers pass it as the same `input.limit`, so the two share one Apollo
// cache entry: opening Translate from the view screen asks the server nothing.
export const GROUP_SCAN = 100;

// The OTHER pages of this page's translation group.
//
// A page with no `translationKey` is in no group and has no siblings — which is
// every page nobody has translated, so the commonest visit to either screen
// answers this without asking the server anything.
//
// The comparison is the stored string, untrimmed, and falsy is ungrouped: it is
// the guard `alternatesOf` makes in api/code/src/render/resolve.js and the one
// `groupKeyOf` makes for the listing, and all three are written the same way on
// purpose. A key of " " groups two pages there, so it groups them here.
export const groupMembers = (rows, page) => {
  const key = page && page.translationKey;

  if (!key) {
    return [];
  }

  return (rows || []).filter(
    (row) => row && row.translationKey === key && row.pageId !== page.pageId
  );
};

// What a site calls one of its languages, or "" for one it does not name.
//
// `label` is optional on a site's locale row and `code` is not, so the code
// leads everywhere it is shown and this is only ever the tail — the same call
// `localeOptions` makes for the pickers and `columnsOf` makes for the
// translation catalog. A code the site no longer declares still has to render,
// so an unknown one is a code with no tail rather than a blank.
export const localeLabel = (site, code) => {
  const declared = ((site && site.locales) || []).find(
    (entry) => entry && entry.code === code
  );

  return (declared && declared.label) || "";
};

// The cell: the badge when there is something to do, "Published" when there
// is not, and nothing for a row that predates publishing — it serves itself,
// and "Published" would be a version it does not have.
export const publishedCell = (member) => {
  const state = publishStateOf(member);

  if (state) {
    return <PublishBadge state={state} />;
  }

  return member && member.published ? (
    <span className="text-muted">Published</span>
  ) : null;
};

// The group, as a table, and how to get to any of it.
//
// Rendered as nothing at all when the group is empty rather than as an empty
// table with a heading: a page nobody has translated should not carry a heading
// about its translations, and that is the whole of what "hide it when there is
// nothing to say" means on both screens.
//
// The language cell holds the RESOLVED code (`localeOf`), not the stored one. A
// page carrying no locale of its own is written in the site's default and says
// so in `<html lang>`; showing the raw empty string as a dash was a column that
// read as "unknown" for the commonest page there is. It is the same resolution
// the listing's chips make.
export const Translations = ({ siteId, site, members, heading }) => {
  if (!members || members.length === 0) {
    return null;
  }

  return (
    <>
      <h6 className="text-muted mt-4 mb-3">{heading}</h6>
      {/* A header row, and not decoration: it is what makes each cell say which
          of the three questions it answers, and it is what an assertion about
          this list can be scoped by — every other listing in this app is a
          table with a heading for the same two reasons. */}
      <Table size="sm" aria-label={heading}>
        <thead>
          <tr>
            <th className="w-25">Language</th>
            <th>Page</th>
            <th>Route</th>
            {/* What a visitor gets of that language — see ../published. The
                page's own screen says it in a sentence; here, beside the
                languages an author is NOT looking at, one word is what makes
                a sibling's unpublished edits visible from this page. */}
            <th>Published</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const code = localeOf(site, member);
            const label = localeLabel(site, code);

            return (
              <tr key={member.pageId}>
                <td>
                  <code>{code}</code>
                  {label ? (
                    <span className="text-muted"> — {label}</span>
                  ) : null}
                </td>
                <td>
                  <Link to={`/site/${siteId}/page/${member.pageId}/`}>
                    {member.title || member.pageId}
                  </Link>
                </td>
                <td className="text-muted">{member.route}</td>
                <td>{publishedCell(member)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};
